// Data Space configuration checker

"use strict";

import { CheckDataSpaceConfigurationResponse } from "@/api/definitions";
import { AppEvents } from "./app-events";
import { Timeouts } from "@/utils/timeout";
import { Request } from "@asanrom/request-browser";
import { ApiAdminDataSpace } from "@/api/api-group-admin-data-space";
import { AuthController } from "./auth";

const CONFIG_CHECK_REQ_ID = "ds-config-check-request";
const CONFIG_CHECK_DEBOUNCE_ID = "ds-config-check-debounce";

// Default status for users that cannot check the configuration (not authenticated
// or lacking the admin.ds permission): treated as configured so the onboarding
// wizard and the missing-configuration banner never show up for them.
function defaultConfigCheckStatus(): CheckDataSpaceConfigurationResponse {
    return { configured: true, steps: [], onboardingDismissed: true };
}

/**
 * Data Space configuration checker
 */
export class DataSpaceConfigurationChecker {
    public static ConfigCheckStatus: CheckDataSpaceConfigurationResponse = defaultConfigCheckStatus();

    // True only while the status above came from a real answer of the server. The default is
    // optimistic, and "cannot be checked" is not evidence that the data space is in order:
    // anything that acts on it being configured has to tell those two apart.
    public static Loaded = false;

    public static Initialize() {
        AppEvents.AddEventListener("auth-status-changed", this.Load.bind(this));

        // The configuration sections emit this after changing anything the check depends on.
        // Without it the missing-configuration banner and the per-section warnings would keep
        // showing the status from the last login, even right after the admin fixed them
        AppEvents.AddEventListener("ds-config-updated", this.LoadDebounced.bind(this));

        // Not every step is decided by something the user does here: did_public_key_registry
        // is resolved against the blockchain and can settle (or expire) on its own, with no
        // event to announce it. Re-checking when the tab comes back to the front keeps the
        // banner honest without polling.
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
                this.LoadDebounced();
            }
        });

        this.Load();
    }

    // A single action emits the event in bursts (the modal saves, its list reloads, a sibling
    // section refreshes), and each check sweeps every configuration step on the backend, so
    // the reloads they trigger are collapsed into one
    private static LoadDebounced() {
        Timeouts.Set(CONFIG_CHECK_DEBOUNCE_ID, 500, this.Load.bind(this));
    }

    public static Load() {
        Timeouts.Abort(CONFIG_CHECK_DEBOUNCE_ID);
        Timeouts.Abort(CONFIG_CHECK_REQ_ID);
        Request.Abort(CONFIG_CHECK_REQ_ID);

        if (!AuthController.isAuthenticated() || !AuthController.hasPermission("admin.ds")) {
            this.ConfigCheckStatus = defaultConfigCheckStatus();
            this.Loaded = false;
            AppEvents.Emit("ds-config-check-updated");
            return;
        }

        Request.Pending(CONFIG_CHECK_REQ_ID, ApiAdminDataSpace.CheckDataSpaceConfiguration())
            .onSuccess((response) => {
                this.ConfigCheckStatus = response;
                this.Loaded = true;

                AppEvents.Emit("ds-config-check-updated");
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        this.ConfigCheckStatus = defaultConfigCheckStatus();
                        this.Loaded = false;
                        AppEvents.Emit("ds-config-check-updated");
                    },
                    forbidden: () => {
                        this.ConfigCheckStatus = defaultConfigCheckStatus();
                        this.Loaded = false;
                        AppEvents.Emit("ds-config-check-updated");
                    },
                    temporalError: () => {
                        Timeouts.Set(CONFIG_CHECK_REQ_ID, 1500, this.Load.bind(this));
                    },
                });
            })
            .onUnexpectedError((err) => {
                console.error(err);
                Timeouts.Set(CONFIG_CHECK_REQ_ID, 1500, this.Load.bind(this));
            });
    }
}
